/**
 * EventPurchasedPage — /events/:slug/thanks
 *
 * Where a paid registration event's Spiffy checkout sends the buyer after
 * they pay (Summit 2026, mct_09f03514-f98). Spiffy appends the order id and
 * email to its thank-you redirect; this page polls
 * GET /api/public/events/:slug/after-purchase until the order:complete webhook
 * has written their registration (usually a few seconds after the redirect),
 * then forwards them into their own registration with a resume token: the
 * thank-you page, which leads with the prep survey and, on an event with
 * gatePage.tierLines, confirms the pass they bought (lib/upgradeStep.ts).
 *
 * The order decides the event, not the URL slug, so a buyer whose order was
 * matched to the other region still lands on the right survey.
 *
 * Fallback: after about a minute, or with no order/email in the URL, the
 * page says their confirmation email is on its way and offers the event page,
 * where entering the email they paid with resumes the same registration.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { useBrandGtm } from './lib/useBrandGtm';
import { useNoIndex } from './lib/useNoIndex';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const MAX_TRIES = 12;
const POLL_MS = 5000;

export default function EventPurchasedPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  useBrandGtm();
  useNoIndex(true);

  const order = params.get('order_id') ?? params.get('order') ?? params.get('id');
  const email = params.get('email') ?? params.get('em');
  const firstName = params.get('name_first') ?? params.get('fn');
  const [gaveUp, setGaveUp] = useState(!order || !email);

  useEffect(() => {
    if (!order || !email) return;
    let live = true;
    let tries = 0;
    let timer = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await fetch(`${API_BASE}/api/public/events/${encodeURIComponent(slug)}/after-purchase?order=${encodeURIComponent(order)}&email=${encodeURIComponent(email)}`);
        const d = await r.json() as { ok?: boolean; ready?: boolean; resumePath?: string };
        if (live && d.ready && d.resumePath) { window.location.replace(d.resumePath); return; }
      } catch { /* retry */ }
      if (!live) return;
      if (tries >= MAX_TRIES) { setGaveUp(true); return; }
      timer = window.setTimeout(tick, POLL_MS);
    };
    timer = window.setTimeout(tick, 1500);
    return () => { live = false; window.clearTimeout(timer); };
  }, [slug, order, email]);

  const btn: CSSProperties = {
    display: 'inline-block', background: '#F05A28', color: '#fff', padding: '14px 32px',
    borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: 'none',
  };

  return (
    <TakerShell>
      <main data-testid="event-purchased" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 16px' }}>
        <div style={{ maxWidth: 520, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <h1 style={{ fontSize: 28, lineHeight: 1.2, margin: 0 }}>
            {firstName ? `Thank you, ${firstName}.` : 'Thank you.'}
          </h1>
          {!gaveUp ? (
            <p style={{ fontSize: 16, lineHeight: 1.6, color: '#374151', margin: 0 }}>
              Your pass is confirmed. Taking you to your Summit page now…
            </p>
          ) : (
            <>
              <p style={{ fontSize: 16, lineHeight: 1.6, color: '#374151', margin: 0 }}>
                One last step: a few quick questions so we can make the Summit count for your practice.
                Your confirmation email is on its way with a link to your survey, or you can start it now
                with the email you used at checkout.
              </p>
              <div><a href={`/events/${encodeURIComponent(slug)}`} style={btn}>Start my survey</a></div>
            </>
          )}
        </div>
      </main>
    </TakerShell>
  );
}
